import React, { useEffect, useRef, useState } from 'react';
import {
  Heading1, Heading2, Heading3, Heading4, Pilcrow, List, ListOrdered, Quote,
  Bold, Italic, Underline, Strikethrough, Code, Sigma, Baseline, Highlighter, RotateCcw,
} from 'lucide-react';
import { TEXT_COLORS, BG_COLORS } from '../../../../lib/richEditor/blocks';

const BLOCK_OPTIONS = [
  { type: 'h1', icon: Heading1, label: 'Heading 1' },
  { type: 'h2', icon: Heading2, label: 'Heading 2' },
  { type: 'h3', icon: Heading3, label: 'Heading 3' },
  { type: 'h4', icon: Heading4, label: 'Heading 4' },
  { type: 'p', icon: Pilcrow, label: 'Text' },
  { type: 'bullet', icon: List, label: 'Bulleted list' },
  { type: 'numbered', icon: ListOrdered, label: 'Numbered list' },
  { type: 'quote', icon: Quote, label: 'Quote' },
];

const FORMAT_OPTIONS = [
  { kind: 'bold', icon: Bold, label: 'Bold' },
  { kind: 'italic', icon: Italic, label: 'Italic' },
  { kind: 'underline', icon: Underline, label: 'Underline' },
  { kind: 'strike', icon: Strikethrough, label: 'Strikethrough' },
  { kind: 'code', icon: Code, label: 'Code' },
];

/** Docked-above formatting bar for RichTextEditor — same icon set as
 * teacher-mobile's NotionToolbar, always visible while a section is being
 * edited (no keyboard-height positioning needed on web).
 *
 * Every button uses onMouseDown/preventDefault: without it, the mousedown
 * blurs the focused block's <input> first (native browser focus-shift on
 * click), clearing the focused block/selection before the click handler
 * ever runs — breaking every button, block-type and format alike.
 */
export default function RichToolbar({ activeBlockType, disabled, onSetBlockType, onApplyInline, onApplyMath }) {
  const [openPanel, setOpenPanel] = useState(null); // null | 'color' | 'bg'
  const panelRef = useRef(null);

  useEffect(() => {
    if (!openPanel) return;
    const onDown = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpenPanel(null); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [openPanel]);

  const renderButton = (key, Icon, label, active, onAction, testId) => (
    <button
      key={key}
      type="button"
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onAction}
      title={label}
      data-testid={testId}
      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
        active ? 'bg-[#34A853]/15 text-[#0d9488]' : 'text-gray-600 hover:bg-gray-100'
      } disabled:opacity-30 disabled:cursor-not-allowed`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  const renderColorPopover = (swatches, kind, testIdPrefix) => (
    <div
      className="absolute top-full left-0 mt-1.5 z-50 flex items-center gap-1 p-1.5 rounded-xl border border-zinc-200 bg-white shadow-[0_8px_24px_rgba(0,0,0,0.10)]"
      data-testid={`${testIdPrefix}-panel`}
    >
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => { onApplyInline(kind, null); setOpenPanel(null); }}
        title="Clear"
        data-testid={`${testIdPrefix}-clear`}
        className="w-6 h-6 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100"
      >
        <RotateCcw className="w-3.5 h-3.5" />
      </button>
      {swatches.map((c) => (
        <button
          key={c.key}
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { onApplyInline(kind, c.key); setOpenPanel(null); }}
          title={c.key}
          data-testid={`${testIdPrefix}-${c.key}`}
          className="w-6 h-6 rounded-full border border-black/10 hover:scale-110 transition-transform"
          style={{ background: c.hex }}
        />
      ))}
    </div>
  );

  return (
    <div className="flex items-center gap-0.5 p-1.5 rounded-xl border border-zinc-200 bg-white flex-wrap relative" data-testid="rich-toolbar" ref={panelRef}>
      {BLOCK_OPTIONS.map(({ type, icon, label }) =>
        renderButton(type, icon, label, activeBlockType === type, () => onSetBlockType(type), `rich-toolbar-block-${type}`))}
      <div className="w-px h-5 bg-zinc-200 mx-1" />
      {FORMAT_OPTIONS.map(({ kind, icon, label }) =>
        renderButton(kind, icon, label, false, () => onApplyInline(kind), `rich-toolbar-format-${kind}`))}
      {renderButton('math', Sigma, 'Equation', false, onApplyMath, 'rich-toolbar-format-math')}
      <div className="relative">
        {renderButton('color', Baseline, 'Text color', openPanel === 'color', () => setOpenPanel((p) => (p === 'color' ? null : 'color')), 'rich-toolbar-format-color')}
        {openPanel === 'color' && renderColorPopover(TEXT_COLORS, 'color', 'rich-toolbar-color')}
      </div>
      <div className="relative">
        {renderButton('bg', Highlighter, 'Highlight', openPanel === 'bg', () => setOpenPanel((p) => (p === 'bg' ? null : 'bg')), 'rich-toolbar-format-bg')}
        {openPanel === 'bg' && renderColorPopover(BG_COLORS, 'bg', 'rich-toolbar-bg')}
      </div>
    </div>
  );
}
